import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { saveAuthSession } from '../utils/authStorage';
import './AuthLayout.css';

const PublicAccessHandler = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, creating, success, error
  const [message, setMessage] = useState('Verifying public access token...');

  useEffect(() => {
    const handlePublicAccess = async () => {
      try {
        if (!token) {
          setStatus('error');
          setMessage('No token provided. Please use a valid public access URL.');
          return;
        }

        // Step 1: Verify token
        setStatus('verifying');
        setMessage('Verifying public access token...');

        // Resolve backend URL - production API at vajraglobal.me
        const apiUrl = process.env.REACT_APP_API_URL || 'https://maintenance.vajraglobal.com';
        
        console.log('🔗 Public Access - Backend URL:', apiUrl);

        const verifyResponse = await fetch(`${apiUrl}/api/public-url/verify?token=${encodeURIComponent(token)}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        const verifyData = await verifyResponse.json();

        if (!verifyData.valid) {
          setStatus('error');
          setMessage(verifyData.message || 'Invalid or revoked public access token.');
          return;
        }

        // Step 2: Create session
        setStatus('creating');
        setMessage('Creating full-access session...');

        const sessionResponse = await fetch(`${apiUrl}/api/public-url/session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token }),
        });

        if (!sessionResponse.ok) {
          const errorData = await sessionResponse.json().catch(() => ({}));
          throw new Error(errorData.message || `HTTP ${sessionResponse.status}`);
        }

        const sessionData = await sessionResponse.json();

        if (sessionData.success) {
          // Save session
          saveAuthSession({
            token: sessionData.token,
            user: sessionData.user,
          });

          setStatus('success');
          setMessage('Access granted! Redirecting to dashboard...');

          // Redirect to dashboard
          setTimeout(() => {
            const redirectUrl = sessionData.redirectUrl || '/';
            navigate(redirectUrl, { replace: true });
          }, 1000);
        } else {
          throw new Error(sessionData.message || 'Failed to create session.');
        }
      } catch (error) {
        console.error('[PUBLIC ACCESS] Error:', error);
        setStatus('error');
        setMessage(error.message || 'Failed to access application. Please try again.');
      }
    };

    handlePublicAccess();
  }, [token, navigate]);

  return (
    <div className="auth-wrapper">
      <div className="auth-card">
        <h1>Public Access</h1>
        {status === 'verifying' && (
          <>
            <p className="auth-subtitle">{message}</p>
            <div style={{ textAlign: 'center', marginTop: '2rem' }}>
              <div className="spinner" style={{ margin: '0 auto' }}></div>
            </div>
          </>
        )}
        {status === 'creating' && (
          <>
            <p className="auth-subtitle">{message}</p>
            <div style={{ textAlign: 'center', marginTop: '2rem' }}>
              <div className="spinner" style={{ margin: '0 auto' }}></div>
            </div>
          </>
        )}
        {status === 'success' && (
          <>
            <p className="auth-subtitle" style={{ color: '#28a745' }}>
              {message}
            </p>
            <div style={{ textAlign: 'center', marginTop: '2rem' }}>
              <div className="spinner" style={{ margin: '0 auto' }}></div>
            </div>
          </>
        )}
        {status === 'error' && (
          <>
            <p className="auth-subtitle" style={{ color: '#dc3545' }}>
              {message}
            </p>
            <div style={{ marginTop: '2rem', textAlign: 'center' }}>
              <button
                onClick={() => navigate('/login')}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#2271b1',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                }}
              >
                Go to Login
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PublicAccessHandler;

